import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const PARTS = [
  { key: "sheeting", label: "Roof Sheeting", desc: "Colorbond steel, fixed to spec", side: "left", top: "8%" },
  { key: "flashing", label: "Flashings", desc: "Custom-folded, watertight detailing", side: "right", top: "26%" },
  { key: "insulation", label: "Insulation & Sarking", desc: "Thermal + moisture barrier", side: "left", top: "46%" },
  { key: "gutter", label: "Gutters", desc: "Sized for Perth downpours", side: "right", top: "66%" },
  { key: "downpipe", label: "Downpipes", desc: "Clean stormwater connection", side: "left", top: "84%" },
];

function AnatomyLabel({ progress, part, index }) {
  const start = index * 0.12;
  const opacity = useTransform(progress, [start, start + 0.18], [0, 1]);
  const x = useTransform(progress, [start, start + 0.18], [part.side === "left" ? -40 : 40, 0]);
  return (
    <motion.div style={{ opacity, x }} className="absolute left-0 right-0" data-testid={`anatomy-${part.key}`}>
      <div className="flex items-start gap-4 bg-white/[0.04] backdrop-blur border border-white/10 rounded-2xl px-5 py-4" style={{ marginTop: part.top }}>
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-xl bg-[#62B848] text-[#0c1a12] font-display font-extrabold">{index + 1}</span>
        <div>
          <div className="font-display font-bold text-lg leading-none mb-1">{part.label}</div>
          <div className="text-white/55 text-sm">{part.desc}</div>
        </div>
      </div>
    </motion.div>
  );
}

export function AnatomyOfRoof() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} data-testid="anatomy-section" className="relative py-28 md:py-40 bg-[#1A2421] text-white overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mb-16">
          <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-3">Anatomy of a roof</p>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-none mb-4">
            Every layer matters.<br />We get them all right.
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            A roof is a system, not a single sheet. Scroll to see the layers that keep Perth homes and commercial assets watertight — each one installed to spec.
          </p>
        </div>

        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative h-[460px] md:h-[560px] rounded-3xl overflow-hidden border border-white/10">
            <motion.img
              src="https://images.unsplash.com/photo-1632759145351-1d592919f522?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Roof system layers"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ scale: imgScale, y: imgY }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A2421] via-transparent to-[#1A2421]/40" />
          </div>

          <div className="relative h-[460px] md:h-[560px]">
            {PARTS.map((p, i) => (
              <AnatomyLabel key={p.key} progress={scrollYProgress} part={p} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
